import React, { useEffect, useState, useCallback } from 'react';
import { Vendor, AMCContract, VendorFilter } from '../../types/vendor';
import { vendorService } from '../../services/vendor/vendorService';
import { VendorTable } from '../../components/vendor/VendorTable';
import { VendorPerformanceDashboard } from '../../components/vendor/VendorPerformanceDashboard';
import { VendorOnboardingModal } from '../../components/vendor/VendorOnboardingModal';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import {
  Plus, TrendingUp, List, FileText, Search, Filter,
  Calendar, DollarSign, CheckCircle2, Clock, AlertTriangle, X
} from 'lucide-react';

type PageTab = 'vendors' | 'performance' | 'contracts';

const CONTRACT_STATUS_CONFIG: Record<AMCContract['status'], { label: string; color: 'success' | 'danger' | 'warning' | 'neutral' | 'info' }> = {
  active: { label: 'Active', color: 'success' },
  expired: { label: 'Expired', color: 'danger' },
  pending: { label: 'Pending', color: 'warning' },
  terminated: { label: 'Terminated', color: 'neutral' },
};

export const VendorManagementPage: React.FC = () => {
  const [tab, setTab] = useState<PageTab>('vendors');
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [contracts, setContracts] = useState<AMCContract[]>([]);
  const [loading, setLoading] = useState(true);
  const [contractsLoading, setContractsLoading] = useState(false);
  const [showOnboardModal, setShowOnboardModal] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [filter, setFilter] = useState<VendorFilter>({});
  const [searchInput, setSearchInput] = useState('');

  const loadVendors = useCallback(async () => {
    setLoading(true);
    try {
      const res = await vendorService.getVendors(filter);
      if (res.success) setVendors(res.data);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const loadContracts = useCallback(async () => {
    setContractsLoading(true);
    try {
      const res = await vendorService.getContracts();
      if (res.success) setContracts(res.data);
    } finally {
      setContractsLoading(false);
    }
  }, []);

  useEffect(() => { loadVendors(); }, [loadVendors]);
  useEffect(() => { loadContracts(); }, [loadContracts]);

  const handleSearch = () => {
    setFilter(prev => ({ ...prev, search: searchInput || undefined }));
  };

  const handleStatusChange = async (vendor: Vendor, status: Vendor['status']) => {
    const res = await vendorService.updateVendorStatus(vendor.vendor_id, status);
    if (res.success) loadVendors();
  };

  const activeContracts = contracts.filter(c => c.status === 'active');
  const totalContractValue = activeContracts.reduce((s, c) => s + c.contract_value, 0);
  const expiringSoon = contracts.filter(c => {
    if (c.status !== 'active') return false;
    const end = new Date(c.end_date);
    const diff = (end.getTime() - Date.now()) / (1000 * 60 * 60 * 24);
    return diff <= 60;
  });

  const tabClass = (t: PageTab) =>
    `flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all ${tab === t
      ? 'bg-sky-500/20 text-sky-400 border border-sky-500/30'
      : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
    }`;

  return (
    <div className="space-y-5">
      {/* Summary KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-3">
          <p className="text-[10px] text-slate-500 font-medium">Total Vendors</p>
          <p className="text-2xl font-semibold text-slate-100 mt-1">{vendors.length}</p>
          <p className="text-[10px] text-emerald-400">{vendors.filter(v => v.status === 'active').length} active</p>
        </div>
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-3">
          <p className="text-[10px] text-slate-500 font-medium">Active AMC Contracts</p>
          <p className="text-2xl font-semibold text-emerald-400 mt-1">{activeContracts.length}</p>
          <p className="text-[10px] text-slate-500">{contracts.length} total</p>
        </div>
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-3">
          <p className="text-[10px] text-slate-500 font-medium">Contract Value (AED)</p>
          <p className="text-2xl font-semibold text-sky-400 mt-1">{(totalContractValue / 1000).toFixed(0)}K</p>
          <p className="text-[10px] text-slate-500">Active contracts</p>
        </div>
        <div className={`border rounded-xl p-3 ${expiringSoon.length > 0 ? 'bg-amber-500/10 border-amber-500/30' : 'bg-slate-900/60 border-slate-800'}`}>
          <p className="text-[10px] text-slate-500 font-medium">Expiring Soon</p>
          <p className={`text-2xl font-semibold mt-1 ${expiringSoon.length > 0 ? 'text-amber-400' : 'text-slate-400'}`}>{expiringSoon.length}</p>
          <p className="text-[10px] text-slate-500">Within 60 days</p>
        </div>
      </div>

      {/* Tab navigation + actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button className={tabClass('vendors')} onClick={() => setTab('vendors')}>
            <List className="w-4 h-4" />Vendors
          </button>
          <button className={tabClass('performance')} onClick={() => setTab('performance')}>
            <TrendingUp className="w-4 h-4" />Performance
          </button>
          <button className={tabClass('contracts')} onClick={() => setTab('contracts')}>
            <FileText className="w-4 h-4" />AMC Contracts
          </button>
        </div>
        <Button variant="primary" icon={<Plus className="w-4 h-4" />} onClick={() => setShowOnboardModal(true)}>
          Onboard Vendor
        </Button>
      </div>

      {/* Vendor List Tab */}
      {tab === 'vendors' && (
        <div className="space-y-4">
          {/* Filter bar */}
          <div className="flex flex-wrap gap-2 items-center">
            <div className="flex flex-1 min-w-[200px] bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
              <input
                type="text"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                placeholder="Search vendorsâ€¦"
                className="flex-1 bg-transparent px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none"
              />
              <button onClick={handleSearch} className="px-3 text-slate-400 hover:text-sky-400 transition-colors">
                <Search className="w-4 h-4" />
              </button>
            </div>
            <select
              value={filter.status || ''}
              onChange={e => setFilter(prev => ({ ...prev, status: (e.target.value as any) || undefined }))}
              className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-300 focus:outline-none focus:border-sky-500"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending_approval">Pending</option>
              <option value="blacklisted">Blacklisted</option>
            </select>
            {(filter.status || filter.search) && (
              <button
                onClick={() => { setFilter({}); setSearchInput(''); }}
                className="flex items-center gap-1 text-xs text-rose-400 hover:text-rose-300 transition-colors"
              >
                <X className="w-3 h-3" />Clear
              </button>
            )}
          </div>

          <VendorTable
            vendors={vendors}
            loading={loading}
            onSelect={setSelectedVendor}
            onStatusChange={handleStatusChange}
          />
        </div>
      )}

      {/* Performance Tab */}
      {tab === 'performance' && (
        <Card title="Vendor Performance Dashboard" subtitle="Aggregate metrics across all active vendors">
          <VendorPerformanceDashboard vendors={vendors} />
        </Card>
      )}

      {/* AMC Contracts Tab */}
      {tab === 'contracts' && (
        <div className="space-y-3">
          {contractsLoading ? (
            <div className="space-y-2">
              {[1, 2].map(i => <div key={i} className="animate-pulse h-24 rounded-xl bg-slate-800/50" />)}
            </div>
          ) : contracts.length === 0 ? (
            <div className="text-center py-12 text-slate-500 text-sm">
              <FileText className="w-8 h-8 mx-auto mb-2 opacity-30" />
              No contracts yet.
            </div>
          ) : (
            contracts.map(contract => {
              const cfg = CONTRACT_STATUS_CONFIG[contract.status];
              const endDate = new Date(contract.end_date);
              const daysLeft = Math.ceil((endDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
              return (
                <div key={contract.contract_id} className="bg-slate-900/70 border border-slate-800 rounded-xl p-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <h4 className="font-semibold text-slate-100 text-sm">{contract.contract_title}</h4>
                      <p className="text-xs text-slate-500 font-mono mt-0.5">{contract.contract_id} Â· {contract.vendor_name}</p>
                    </div>
                    <Badge label={cfg.label} variant={cfg.color} />
                  </div>
                  <p className="text-xs text-slate-400 mb-3 line-clamp-2">{contract.scope_of_work}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                    <div className="flex items-center gap-1.5 text-slate-400">
                      <DollarSign className="w-3 h-3 text-emerald-400" />
                      <span>{contract.currency} {contract.contract_value.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-slate-400">
                      <Calendar className="w-3 h-3 text-sky-400" />
                      <span>{contract.start_date} â†’ {contract.end_date}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-slate-400">
                      <Clock className="w-3 h-3 text-purple-400" />
                      <span>SLA: {contract.sla_response_hours}h response / {contract.sla_completion_hours}h completion</span>
                    </div>
                    <div className={`flex items-center gap-1.5 ${daysLeft <= 60 && contract.status === 'active' ? 'text-amber-400' : 'text-slate-400'}`}>
                      {daysLeft <= 60 && contract.status === 'active'
                        ? <AlertTriangle className="w-3 h-3" />
                        : <CheckCircle2 className="w-3 h-3 text-emerald-400" />}
                      <span>
                        {contract.status === 'active'
                          ? daysLeft > 0 ? `${daysLeft} days left` : 'Expired'
                          : contract.status}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Vendor Detail Slide-over (simple inline) */}
      {selectedVendor && (
        <div className="fixed inset-0 z-40 flex bg-slate-950/70 backdrop-blur-sm"
          onClick={() => setSelectedVendor(null)}>
          <div className="ml-auto w-full max-w-md h-full bg-slate-950 border-l border-slate-800 overflow-y-auto p-6 space-y-5"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-100">Vendor Detail</h3>
              <button onClick={() => setSelectedVendor(null)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-xs text-slate-500">Company</p>
                <p className="font-semibold text-slate-100">{selectedVendor.name}</p>
                <p className="text-xs font-mono text-sky-400">{selectedVendor.vendor_id}</p>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><p className="text-slate-500">Contact</p><p className="text-slate-300">{selectedVendor.contact_person}</p></div>
                <div><p className="text-slate-500">Phone</p><p className="text-slate-300">{selectedVendor.contact_phone}</p></div>
                <div><p className="text-slate-500">Email</p><p className="text-slate-300 break-all">{selectedVendor.contact_email}</p></div>
                <div><p className="text-slate-500">License</p><p className="text-slate-300">{selectedVendor.trade_license_no}</p></div>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1.5">Category Specializations</p>
                <div className="flex flex-wrap gap-1.5">
                  {selectedVendor.category_mappings.map(c => (
                    <span key={c.category_id} className="px-2 py-0.5 rounded-full text-[10px] bg-sky-500/10 text-sky-400 border border-sky-500/20">{c.category_name}</span>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1.5">Service Areas</p>
                <div className="flex flex-wrap gap-1.5">
                  {selectedVendor.service_areas.map(a => (
                    <span key={a.location_id} className="px-2 py-0.5 rounded-full text-[10px] bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">{a.location_name}</span>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-2">Performance Metrics</p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {[
                    { label: 'Avg Response', value: `${selectedVendor.performance.avg_response_time_hours.toFixed(1)}h`, colorClass: 'text-sky-400' },
                    { label: 'On-Time Rate', value: `${selectedVendor.performance.on_time_completion_rate.toFixed(1)}%`, colorClass: 'text-emerald-400' },
                    { label: 'Pass Rate', value: `${selectedVendor.performance.inspection_pass_rate.toFixed(1)}%`, colorClass: 'text-violet-400' },
                    { label: 'Cost Variance', value: `${selectedVendor.performance.cost_variance_percent > 0 ? '+' : ''}${selectedVendor.performance.cost_variance_percent.toFixed(1)}%`, colorClass: selectedVendor.performance.cost_variance_percent > 5 ? 'text-rose-400' : 'text-emerald-400' },
                    { label: 'Jobs Done', value: `${selectedVendor.performance.total_jobs_completed}/${selectedVendor.performance.total_jobs_assigned}`, colorClass: 'text-amber-400' },
                    { label: 'Rating', value: `${selectedVendor.performance.rating.toFixed(1)} â˜…`, colorClass: 'text-amber-400' },
                  ].map(m => (
                    <div key={m.label} className="bg-slate-900 border border-slate-800 rounded-lg p-2">
                      <p className="text-slate-500 text-[10px]">{m.label}</p>
                      <p className={`font-semibold text-sm mt-0.5 ${m.colorClass}`}>{m.value}</p>
                    </div>
                  ))}
                </div>
              </div>
              {selectedVendor.notes && (
                <div>
                  <p className="text-xs text-slate-500 mb-1">Notes</p>
                  <p className="text-xs text-slate-400 bg-slate-900 border border-slate-800 rounded-lg p-3">{selectedVendor.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Onboarding Modal */}
      {showOnboardModal && (
        <VendorOnboardingModal
          onClose={() => setShowOnboardModal(false)}
          onSuccess={() => { loadVendors(); setTab('vendors'); }}
        />
      )}
    </div>
  );
};
